import { API_BASE_URL } from "../config/api";
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import EngineerLayout from "../components/EngineerLayout";
import PhoneActions from "../components/PhoneActions";
import { getPrimaryCustomerContact } from "../utils/customerForm";

const formatLocation = (address) => {
  if (!address) return "-";

  if (typeof address === "string") {
    return address || "-";
  }

  return [address.city, address.state].filter(Boolean).join(", ")
    || [address.street, address.area, address.city].filter(Boolean).join(", ")
    || "-";
};

export default function Customers() {
  const role = localStorage.getItem("role") || "";
  const location = useLocation();
  const canManageCustomers = role === "admin" || role === "accounts";
  const isAccountsView = role === "accounts";
  const isEngineerRoute = location.pathname.startsWith("/engineer");
  const isEngineerView = role === "engineer" && isEngineerRoute;
  const engineerLookup =
    localStorage.getItem("engineerId") ||
    localStorage.getItem("engineerEmail") ||
    localStorage.getItem("engineerName") ||
    "";
  const cachedEngineerCustomerAccess = localStorage.getItem("engineerCustomerViewEnabled");
  const [customers, setCustomers] = useState([]);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("name-asc");
  const [loading, setLoading] = useState(true);
  const [onboardingLink, setOnboardingLink] = useState("");
  const [showOnboardingModal, setShowOnboardingModal] = useState(false);
  const [generatingLink, setGeneratingLink] = useState(false);
  const [onboardingLinks, setOnboardingLinks] = useState([]);
  const [loadingLinks, setLoadingLinks] = useState(false);
  const [collapsedGroups, setCollapsedGroups] = useState({});
  const navigate = useNavigate();
  const [viewTab, setViewTab] = useState("customers");

  const fetchOnboardingLinks = async () => {
    try {
      setLoadingLinks(true);
      const token = localStorage.getItem("token");
      const res = await axios.get(API_BASE_URL + "/api/customers/onboarding/links", {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOnboardingLinks(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingLinks(false);
    }
  };

  const handleGenerateOnboardingLink = () => {
    setShowOnboardingModal(true);
    fetchOnboardingLinks();
  };

  const handleGenerateOnboardingLinkSubmit = async () => {
    try {
      setGeneratingLink(true);
      const token = localStorage.getItem("token");
      const res = await axios.post(
        API_BASE_URL + "/api/customers/onboarding/generate",
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setOnboardingLink(res.data.link);
      fetchOnboardingLinks();
    } catch (error) {
      console.error(error);
      alert(error.response?.data?.message || "Failed to generate onboarding link");
    } finally {
      setGeneratingLink(false);
    }
  };

  const handleDeactivateLink = async (linkToken) => {
    if (!window.confirm("Are you sure you want to deactivate this onboarding link?")) {
      return;
    }
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API_BASE_URL}/api/customers/onboarding/deactivate/${linkToken}`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      fetchOnboardingLinks();
    } catch (error) {
      console.error(error);
      alert(error.response?.data?.message || "Failed to deactivate onboarding link.");
    }
  };

  const handleRegenerateLink = async (linkToken) => {
    if (!window.confirm("Are you sure you want to regenerate this link? The current link will be deactivated.")) {
      return;
    }
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(
        `${API_BASE_URL}/api/customers/onboarding/regenerate/${linkToken}`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setOnboardingLink(res.data.link);
      fetchOnboardingLinks();
    } catch (error) {
      console.error(error);
      alert(error.response?.data?.message || "Failed to regenerate onboarding link.");
    }
  };

  const handleClearHistory = async () => {
    if (!window.confirm("Are you sure you want to clear used, expired, and deactivated onboarding links from history?")) {
      return;
    }
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API_BASE_URL}/api/customers/onboarding/clear`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert("Inactive onboarding links cleared successfully.");
      fetchOnboardingLinks();
    } catch (error) {
      console.error(error);
      alert(error.response?.data?.message || "Failed to clear history.");
    }
  };


  useEffect(() => {
    if (role === "engineer" && !isEngineerRoute) {
      navigate("/engineer/customers", { replace: true });
    }
  }, [isEngineerRoute, navigate, role]);

  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        if (isEngineerView && engineerLookup) {
          if (cachedEngineerCustomerAccess === "false") {
            navigate("/engineer-dashboard", { replace: true });
            setCustomers([]);
            return;
          }

          const [customersRes, engineerRes] = await Promise.all([
            axios.get(API_BASE_URL + "/api/customers"),
            axios.get(API_BASE_URL + `/api/engineers/profile/${encodeURIComponent(engineerLookup)}`)
          ]);

          const customerViewEnabled = Boolean(engineerRes.data?.customerViewEnabled);
          localStorage.setItem("engineerCustomerViewEnabled", String(customerViewEnabled));
          if (!customerViewEnabled) {
            setCustomers([]);
            navigate("/engineer-dashboard", { replace: true });
            return;
          }

          setCustomers(Array.isArray(customersRes.data) ? customersRes.data : []);
          return;
        }

        const customersRes = await axios.get(API_BASE_URL + "/api/customers");
        setCustomers(Array.isArray(customersRes.data) ? customersRes.data : []);
      } catch (error) {
        console.error("Fetch error:", error);
        setCustomers([]);
      } finally {
        setLoading(false);
      }
    };

    fetchCustomers();
  }, [cachedEngineerCustomerAccess, engineerLookup, isEngineerView, navigate]);

  const filteredCustomers = useMemo(() => {
    const query = search.trim().toLowerCase();

    return customers.filter((customer) => {
      const parts = [
        customer.customerId,
        customer.name,
        customer.email,
        customer.contactPerson,
        customer.phone,
        formatLocation(customer.address),
        customer.additionalInfo,
        customer.gstNumber || "",
        customer.groupId?.groupName || ""
      ];
      return parts.join(" ").toLowerCase().includes(query);
    });
  }, [customers, search]);

  const sortedCustomers = useMemo(() => {
    const query = search.trim().toLowerCase();

    if (query) {
      const getRelevanceScore = (customer) => {
        const name = String(customer.name || "").trim().toLowerCase();
        if (name === query) return 0;
        if (name.startsWith(query)) return 1;

        const words = name.split(/[\s/\-()]+/);
        if (words.some((word) => word.startsWith(query))) return 2;

        if (name.includes(query)) return 3;

        return 4;
      };

      return [...filteredCustomers].sort((left, right) => {
        const leftScore = getRelevanceScore(left);
        const rightScore = getRelevanceScore(right);
        if (leftScore !== rightScore) {
          return leftScore - rightScore;
        }

        const leftValue = String(left.name || left.customerId || "");
        const rightValue = String(right.name || right.customerId || "");
        return leftValue.localeCompare(rightValue, undefined, { numeric: true, sensitivity: "base" });
      });
    }

    const getSortValue = (customer) => {
      if (sortBy === "customer-id") {
        return String(customer.customerId || "");
      }

      if (sortBy === "name-desc" || sortBy === "name-asc") {
        return String(customer.name || customer.customerId || "");
      }

      return String(customer.name || customer.customerId || "");
    };

    const direction = sortBy === "name-desc" ? -1 : 1;
    return [...filteredCustomers].sort((left, right) => {
      const leftValue = getSortValue(left);
      const rightValue = getSortValue(right);
      const primary = leftValue.localeCompare(rightValue, undefined, { numeric: true, sensitivity: "base" });
      if (primary !== 0) {
        return primary * direction;
      }

      return String(left.customerId || "").localeCompare(String(right.customerId || ""), undefined, { numeric: true, sensitivity: "base" });
    });
  }, [filteredCustomers, search, sortBy]);

  const groupedCustomers = useMemo(() => {
    const groupsMap = {};
    const ungrouped = [];

    sortedCustomers.forEach((customer) => {
      if (customer.groupId && customer.groupId._id) {
        const gId = customer.groupId._id;
        if (!groupsMap[gId]) {
          groupsMap[gId] = {
            _id: gId,
            groupName: customer.groupId.groupName,
            description: customer.groupId.description,
            customers: []
          };
        }
        groupsMap[gId].customers.push(customer);
      } else {
        ungrouped.push(customer);
      }
    });

    return {
      groups: Object.values(groupsMap).sort((a, b) => a.groupName.localeCompare(b.groupName)),
      ungrouped
    };
  }, [sortedCustomers]);

  const toggleGroup = (groupId) => {
    setCollapsedGroups((prev) => ({
      ...prev,
      [groupId]: !prev[groupId]
    }));
  };

  const getPrimaryContactLabel = (customer) => {
    const primaryContact = getPrimaryCustomerContact(customer);
    if (!primaryContact) return "-";

    const pieces = [primaryContact.role, primaryContact.name, primaryContact.mobileNumber].filter(Boolean);
    return pieces.join(" · ") || "-";
  };


  const content = (
    <div className="space-y-4 lg:space-y-6">
      <section className="theme-hero rounded-xl border border-slate-200 bg-white p-4 shadow-sm lg:rounded-[28px] lg:bg-[linear-gradient(135deg,_rgba(255,255,255,1),_rgba(248,250,252,1),_rgba(239,246,255,0.95))] lg:p-6">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <p className="hidden text-sm font-semibold uppercase tracking-[0.22em] text-sky-700 lg:block">
              Customer Directory
            </p>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4 mt-1 lg:mt-2">
              <h1 className="text-2xl font-bold text-slate-900 lg:text-3xl">Customer Directory</h1>
              <div className="inline-flex rounded-lg bg-slate-100 p-0.5 border border-slate-200 self-start">
                <button
                  type="button"
                  onClick={() => setViewTab("customers")}
                  className={`rounded-md px-3.5 py-1 text-xs font-semibold transition-all duration-200 ${
                    viewTab === "customers"
                      ? "bg-white text-sky-700 shadow-sm"
                      : "text-slate-600 hover:text-slate-900"
                  }`}
                >
                  Customers
                </button>
                <button
                  type="button"
                  onClick={() => setViewTab("grouped")}
                  className={`rounded-md px-3.5 py-1 text-xs font-semibold transition-all duration-200 ${
                    viewTab === "grouped"
                      ? "bg-white text-sky-700 shadow-sm"
                      : "text-slate-600 hover:text-slate-900"
                  }`}
                >
                  Grouped
                </button>
              </div>
            </div>
          </div>

          <div className="flex w-full flex-col gap-2 lg:w-auto lg:flex-row lg:items-end">
            <input
              type="text"
              placeholder="Search by name, customer ID, etc..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="min-w-0 flex-1 rounded-lg border border-slate-300 bg-white px-4 py-3 text-sm outline-none transition focus:border-sky-400 focus:bg-white focus:ring-4 focus:ring-sky-100 lg:w-80 lg:rounded-2xl"
            />

            <label className="block lg:min-w-[220px]">
              <span className="mb-2 block text-xs font-semibold uppercase tracking-wider text-slate-500">Sort</span>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="w-full rounded-lg border border-slate-300 bg-white px-4 py-3 text-sm font-medium text-slate-700 outline-none transition focus:border-sky-400 focus:ring-4 focus:ring-sky-100 lg:rounded-2xl"
              >
                <option value="name-asc">Name A-Z</option>
                <option value="name-desc">Name Z-A</option>
                <option value="customer-id">Customer ID</option>
              </select>
            </label>

            {canManageCustomers && (
              <div className="hidden lg:flex lg:items-center lg:gap-2">
                <button
                  type="button"
                  onClick={handleGenerateOnboardingLink}
                  className="inline-flex items-center justify-center rounded-2xl border border-slate-200 bg-white px-5 py-3 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
                >
                  🔗 Onboarding Link
                </button>
                <button
                  onClick={() => navigate(isAccountsView ? "/accounts/create-customer" : "/create-customer")}
                  className="inline-flex items-center justify-center rounded-2xl bg-emerald-600 px-5 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-700"
                >
                  + Add Customer
                </button>
              </div>
            )}
          </div>
        </div>
        {canManageCustomers && (
          <div className="mt-3 flex gap-2 lg:hidden">
            <button
              type="button"
              onClick={handleGenerateOnboardingLink}
              className="flex-1 rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
            >
              🔗 Onboarding Link
            </button>
            <button
              onClick={() => navigate(isAccountsView ? "/accounts/create-customer" : "/create-customer")}
              className="flex-1 rounded-lg bg-emerald-600 px-4 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-700"
            >
              + Add Customer
            </button>
          </div>
        )}
      </section>

      {loading ? (
        <section className="theme-panel rounded-[28px] border border-slate-200 bg-white px-5 py-12 text-center text-slate-500 shadow-sm">
          Loading customers...
        </section>
      ) : filteredCustomers.length === 0 ? (
        <section className="theme-panel rounded-[28px] border border-slate-200 bg-white px-5 py-12 text-center text-slate-500 shadow-sm">
          No customers found
        </section>
      ) : (
        <div className="space-y-4">
          {viewTab === "customers" ? (
            <>
              {/* Flat mobile list */}
              <div className="space-y-3 lg:hidden">
                {sortedCustomers.map((customer) => (
                  <CustomerMobileCard
                    key={customer._id}
                    customer={customer}
                    showGroupBadge={true}
                    onOpen={() => navigate(
                      isEngineerView
                        ? `/engineer/customer/${customer._id}`
                        : isAccountsView
                        ? `/accounts/customer/${customer._id}`
                        : `/customer/${customer._id}`
                    )}
                  />
                ))}
              </div>
              {/* Flat desktop grid */}
              <div className="hidden grid-cols-1 gap-4 lg:grid xl:grid-cols-2">
                {sortedCustomers.map((customer) => (
                  <article
                    key={customer._id}
                    role="button"
                    tabIndex={0}
                    onClick={() => navigate(
                      isEngineerView
                        ? `/engineer/customer/${customer._id}`
                        : isAccountsView
                        ? `/accounts/customer/${customer._id}`
                        : `/customer/${customer._id}`
                    )}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        navigate(
                          isEngineerView
                            ? `/engineer/customer/${customer._id}`
                            : isAccountsView
                            ? `/accounts/customer/${customer._id}`
                            : `/customer/${customer._id}`
                        );
                      }
                    }}
                    className="theme-panel cursor-pointer rounded-[22px] border border-slate-200 bg-white p-4 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-sky-300"
                  >
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-400">
                            {customer.customerId || "Customer"}
                          </p>
                          {customer.groupId?.groupName && (
                            <span className="inline-flex items-center gap-1 rounded-md bg-blue-50 px-2 py-0.5 text-xs font-semibold text-blue-700 ring-1 ring-inset ring-blue-700/10">
                              🏢 {customer.groupId.groupName}
                            </span>
                          )}
                        </div>
                        <h2 className="mt-1 truncate whitespace-nowrap text-xl font-bold text-slate-900 sm:text-xl">
                          {customer.name || "-"}
                        </h2>
                        <p className="mt-1 text-sm text-slate-500">{formatLocation(customer.address)}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => navigate(
                          isEngineerView
                            ? `/engineer/customer/${customer._id}`
                            : isAccountsView
                            ? `/accounts/customer/${customer._id}`
                            : `/customer/${customer._id}`
                        )}
                        className="shrink-0 rounded-xl bg-sky-600 px-3.5 py-2 text-sm font-semibold text-white transition hover:bg-sky-700"
                      >
                        View Profile
                      </button>
                    </div>
                    <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
                      <InfoCard compact label="Primary Contact" value={getPrimaryContactLabel(customer)} />
                      <InfoCard compact label="Phone" value={<PhoneActions phone={customer.phone || ""} />} />
                      <InfoCard compact label="Email" value={customer.email || "-"} />
                      <InfoCard compact label="Additional Info" value={customer.additionalInfo || "-"} />
                    </div>
                  </article>
                ))}
              </div>
            </>
          ) : (
            <>
              {groupedCustomers.groups.map((group) => {
                const isCollapsed = collapsedGroups[group._id];
                return (
                  <div key={group._id} className="rounded-2xl border border-slate-200 bg-white overflow-hidden shadow-sm">
                    <button
                      type="button"
                      onClick={() => toggleGroup(group._id)}
                      className="flex w-full items-center justify-between bg-slate-50 px-6 py-4 transition hover:bg-slate-100/50"
                    >
                      <div className="flex items-center gap-3">
                        <span className="material-symbols-outlined text-sky-600">corporate_fare</span>
                        <div className="text-left">
                          <h2 className="text-lg font-bold text-slate-900">{group.groupName}</h2>
                          {group.description && <p className="text-xs text-slate-500 mt-0.5">{group.description}</p>}
                        </div>
                        <span className="inline-flex items-center rounded-full bg-sky-100 px-2.5 py-0.5 text-xs font-bold text-sky-700">
                          {group.customers.length} {group.customers.length === 1 ? "Customer" : "Customers"}
                        </span>
                      </div>
                      <span className="material-symbols-outlined transition-transform duration-205" style={{ transform: isCollapsed ? "rotate(-90deg)" : "rotate(0deg)" }}>
                        expand_more
                      </span>
                    </button>

                    {!isCollapsed && (
                      <div className="p-6 border-t border-slate-100">
                        {/* Mobile lists (lg:hidden) */}
                        <div className="space-y-3 lg:hidden">
                          {group.customers.map((customer) => (
                            <CustomerMobileCard
                              key={customer._id}
                              customer={customer}
                              showGroupBadge={false}
                              onOpen={() => navigate(
                                isEngineerView
                                  ? `/engineer/customer/${customer._id}`
                                  : isAccountsView
                                  ? `/accounts/customer/${customer._id}`
                                  : `/customer/${customer._id}`
                              )}
                            />
                          ))}
                        </div>
                        {/* Desktop grids (hidden lg:grid) */}
                        <div className="hidden grid-cols-1 gap-4 lg:grid xl:grid-cols-2">
                          {group.customers.map((customer) => (
                            <article
                              key={customer._id}
                              role="button"
                              tabIndex={0}
                              onClick={() => navigate(
                                isEngineerView
                                  ? `/engineer/customer/${customer._id}`
                                  : isAccountsView
                                  ? `/accounts/customer/${customer._id}`
                                  : `/customer/${customer._id}`
                              )}
                              onKeyDown={(event) => {
                                if (event.key === "Enter" || event.key === " ") {
                                  event.preventDefault();
                                  navigate(
                                    isEngineerView
                                      ? `/engineer/customer/${customer._id}`
                                      : isAccountsView
                                      ? `/accounts/customer/${customer._id}`
                                      : `/customer/${customer._id}`
                                  );
                                }
                              }}
                              className="theme-panel cursor-pointer rounded-[22px] border border-slate-200 bg-white p-4 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-sky-300"
                            >
                              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                                <div className="min-w-0 flex-1">
                                  <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-400">
                                    {customer.customerId || "Customer"}
                                  </p>
                                  <h2 className="mt-1 truncate whitespace-nowrap text-xl font-bold text-slate-900 sm:text-xl">
                                    {customer.name || "-"}
                                  </h2>
                                  <p className="mt-1 text-sm text-slate-500">{formatLocation(customer.address)}</p>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => navigate(
                                    isEngineerView
                                      ? `/engineer/customer/${customer._id}`
                                      : isAccountsView
                                      ? `/accounts/customer/${customer._id}`
                                      : `/customer/${customer._id}`
                                  )}
                                  className="shrink-0 rounded-xl bg-sky-600 px-3.5 py-2 text-sm font-semibold text-white transition hover:bg-sky-700"
                                >
                                  View Profile
                                </button>
                              </div>
                              <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
                                <InfoCard compact label="Primary Contact" value={getPrimaryContactLabel(customer)} />
                                <InfoCard compact label="Phone" value={<PhoneActions phone={customer.phone || ""} />} />
                                <InfoCard compact label="Email" value={customer.email || "-"} />
                                <InfoCard compact label="Additional Info" value={customer.additionalInfo || "-"} />
                              </div>
                            </article>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </>
          )}
        </div>
      )}

      {showOnboardingModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
          <div className="w-full max-w-4xl rounded-[28px] border border-slate-100 bg-white p-6 shadow-xl text-left max-h-[90vh] flex flex-col">
            <div className="flex justify-between items-start">
              <div>
                <h2 className="text-xl font-bold text-slate-900">🔗 Customer Onboarding Links</h2>
                <p className="mt-1 text-sm text-slate-500">
                  Generate secure, single-use registration links for clients. Once used, the link expires automatically.
                </p>
              </div>
              <button
                onClick={() => {
                  setShowOnboardingModal(false);
                  setOnboardingLink("");
                }}
                className="text-slate-400 hover:text-slate-600 transition"
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="h-6 w-6">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="mt-5 flex flex-col md:flex-row gap-4 items-end bg-slate-50 p-4 rounded-2xl border border-slate-100">
              <div className="flex-1">
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">New Onboarding Link</label>
                <input
                  type="text"
                  readOnly
                  value={onboardingLink || "Click Generate to create a secure link..."}
                  className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-700 outline-none"
                />
              </div>
              <div className="flex gap-2">
                {onboardingLink && (
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(onboardingLink);
                      alert("Onboarding link copied to clipboard!");
                    }}
                    className="rounded-xl bg-slate-200 text-slate-700 px-4 py-2.5 text-sm font-semibold hover:bg-slate-300 transition"
                  >
                    Copy
                  </button>
                )}
                <button
                  onClick={handleGenerateOnboardingLinkSubmit}
                  disabled={generatingLink}
                  className="rounded-xl bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-blue-700 disabled:bg-slate-400 flex items-center gap-2"
                >
                  {generatingLink ? "Generating..." : onboardingLink ? "Regenerate" : "Generate Link"}
                </button>
              </div>
            </div>

            <div className="mt-6 flex-1 overflow-hidden flex flex-col">
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wider">Onboarding Link Tracker</h3>
                {onboardingLinks.length > 0 && (
                  <button
                    type="button"
                    onClick={handleClearHistory}
                    className="text-xs font-semibold text-red-600 hover:text-red-800 transition"
                  >
                    Clear Inactive Links
                  </button>
                )}
              </div>
              <div className="flex-1 overflow-x-auto overflow-y-auto border border-slate-100 rounded-2xl min-h-[250px]">
                {loadingLinks ? (
                  <div className="p-8 text-center text-slate-500">Loading tracking data...</div>
                ) : onboardingLinks.length === 0 ? (
                  <div className="p-8 text-center text-slate-500">No onboarding links generated yet.</div>
                ) : (
                  <table className="w-full min-w-[900px] border-collapse text-left text-sm text-slate-700">
                    <thead className="sticky top-0 bg-slate-50 font-semibold text-slate-700 border-b border-slate-100 z-10">
                      <tr>
                        <th className="px-4 py-3">Link / Token</th>
                        <th className="px-4 py-3">Created</th>
                        <th className="px-4 py-3">Expiry</th>
                        <th className="px-4 py-3">Status</th>
                        <th className="px-4 py-3">Customer Created</th>
                        <th className="px-4 py-3 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {onboardingLinks.map((link) => {
                        const isExpired = new Date() > new Date(link.expiresAt) && link.status === "Pending";
                        const displayStatus = isExpired ? "Expired" : link.status;
                        
                        let statusColor = "bg-slate-50 text-slate-600 border-slate-200";
                        if (displayStatus === "Pending") statusColor = "bg-amber-50 text-amber-700 border-amber-200";
                        else if (displayStatus === "Used" || displayStatus === "submitted") statusColor = "bg-emerald-50 text-emerald-700 border-emerald-200";
                        else if (displayStatus === "Expired" || displayStatus === "expired") statusColor = "bg-rose-50 text-rose-700 border-rose-200";
                        else if (displayStatus === "Deactivated") statusColor = "bg-slate-100 text-slate-500 border-slate-200";

                        const absoluteLink = `${window.location.origin}/onboarding/${link.token}`;

                        return (
                          <tr key={link._id} className="hover:bg-slate-50/50 transition">
                            <td className="px-4 py-3 font-mono text-xs max-w-[120px] truncate" title={absoluteLink}>
                              {link.token.slice(0, 8)}...
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              {new Date(link.createdAt).toLocaleDateString("en-GB", { day: "2-digit", month: "short" })}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              {new Date(link.expiresAt).toLocaleDateString("en-GB", { day: "2-digit", month: "short" })}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <span className={`inline-flex rounded-full border px-2 py-0.5 text-xs font-semibold ${statusColor}`}>
                                {displayStatus}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              {link.customerId ? (
                                <button
                                  type="button"
                                  onClick={() => {
                                    setShowOnboardingModal(false);
                                    navigate(
                                      isAccountsView
                                        ? `/accounts/customer/${link.customerId._id}`
                                        : `/customer/${link.customerId._id}`
                                    );
                                  }}
                                  className="text-left font-semibold text-blue-600 hover:text-blue-800 underline truncate max-w-[150px] block"
                                >
                                  {link.customerId.name} ({link.customerId.customerId})
                                </button>
                              ) : (
                                <span className="text-slate-400">-</span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-right whitespace-nowrap">
                              <div className="flex justify-end gap-1.5">
                                <button
                                  type="button"
                                  onClick={() => {
                                    navigator.clipboard.writeText(absoluteLink);
                                    alert("Onboarding link copied!");
                                  }}
                                  title="Copy Link"
                                  className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100 hover:text-slate-700 transition"
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="h-4 w-4">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 17.25v3.375c0 .621-.504 1.125-1.125 1.125h-9.75a1.125 1.125 0 0 1-1.125-1.125V7.875c0-.621.504-1.125 1.125-1.125H5.25m11.9-3.664A2.251 2.251 0 0 0 15 2.25h-1.5a2.251 2.251 0 0 0-2.15 1.586m5.8 0c.065.21.1.433.1.664v.75h-6V4.5c0-.231.035-.454.1-.664M6.75 7.5H18a2.25 2.25 0 0 1 2.25 2.25v10.5A2.25 2.25 0 0 1 18 22.5H6.75a2.25 2.25 0 0 1-2.25-2.25v-10.5A2.25 2.25 0 0 1 6.75 7.5Z" />
                                  </svg>
                                </button>
                                {link.customerId && (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setShowOnboardingModal(false);
                                      navigate(
                                        isAccountsView
                                          ? `/accounts/customer/${link.customerId._id}`
                                          : `/customer/${link.customerId._id}`
                                      );
                                    }}
                                    title="View Submission"
                                    className="rounded-lg p-1.5 text-sky-600 hover:bg-sky-50 hover:text-sky-700 transition"
                                  >
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="h-4 w-4">
                                      <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z" />
                                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
                                    </svg>
                                  </button>
                                )}
                                {displayStatus === "Pending" && (
                                  <>
                                    <button
                                      type="button"
                                      onClick={() => handleDeactivateLink(link.token)}
                                      title="Deactivate Link"
                                      className="rounded-lg p-1.5 text-rose-600 hover:bg-rose-50 hover:text-rose-700 transition"
                                    >
                                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="h-4 w-4">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M18.364 18.364A9 9 0 0 0 5.636 5.636m12.728 12.728A9 9 0 0 1 5.636 5.636m12.728 12.728L5.636 5.636" />
                                      </svg>
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => handleRegenerateLink(link.token)}
                                      title="Regenerate Link"
                                      className="rounded-lg p-1.5 text-amber-600 hover:bg-amber-50 hover:text-amber-700 transition"
                                    >
                                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="h-4 w-4">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99" />
                                      </svg>
                                    </button>
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </div>

            <div className="mt-6 flex justify-end">
              <button
                onClick={() => {
                  setShowOnboardingModal(false);
                  setOnboardingLink("");
                }}
                className="rounded-xl border border-slate-200 bg-white px-5 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 shadow-sm"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  return isEngineerView ? <EngineerLayout>{content}</EngineerLayout> : content;
}

function CustomerMobileCard({ customer, onOpen, showGroupBadge = false }) {
  return (
    <article
      role="button"
      tabIndex={0}
      className="cursor-pointer rounded-xl bg-white p-4 shadow-sm ring-1 ring-slate-100"
      onClick={onOpen}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onOpen?.();
        }
      }}
    >
      <div className="flex items-center gap-2 flex-wrap">
        <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-400">
          {customer.customerId || "Customer"}
        </p>
        {showGroupBadge && customer.groupId?.groupName && (
          <span className="inline-flex items-center gap-1 rounded-md bg-blue-50 px-2 py-0.5 text-xs font-semibold text-blue-700 ring-1 ring-inset ring-blue-700/10">
            🏢 {customer.groupId.groupName}
          </span>
        )}
      </div>
      <h2 className="mt-2 text-lg font-bold leading-snug text-slate-900">{customer.name || "-"}</h2>
      <div className="mt-3 rounded-xl border border-slate-100 bg-slate-50 px-3 py-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">Primary Contact</p>
            <p className="mt-1 text-sm text-slate-700">{getPrimaryCustomerContact(customer)?.name || customer.contactPerson || "-"}</p>
            <div className="mt-2 text-sm text-slate-700">
              <PhoneActions phone={customer.phone || ""} />
            </div>
          </div>
        </div>
      </div>
      <div className="mt-3 flex items-center justify-between gap-3 text-sm">
        <span className="font-semibold uppercase tracking-[0.18em] text-slate-500">Additional Info</span>
        <span className="truncate text-slate-700">{customer.additionalInfo || "-"}</span>
      </div>
    </article>
  );
}

function InfoCard({ label, value, compact = false }) {
  return (
    <div className={`theme-panel rounded-2xl border border-slate-200 bg-slate-50 ${compact ? "p-3" : "p-4"}`}>
      <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">{label}</p>
      <div className={`mt-1 text-sm text-slate-700 ${compact ? "leading-5" : "leading-6"}`}>{value}</div>
    </div>
  );
}


